import type { AuthFetchConfig, TokenData } from './types'

/**
 * Cria um wrapper de fetch com autenticação automática e retry em 401
 * 
 * @example
 * ```ts
 * const authFetch = createAuthFetch({
 *   getToken: () => authStore.accessToken,
 *   getRefreshToken: () => authStore.refreshToken,
 *   refreshUrl: '/sessions/refresh',
 *   onTokenRefreshed: (tokens) => authStore.updateTokens(tokens),
 *   onAuthFailed: () => router.push('/login'),
 * })
 * 
 * // Usar como fetch normal
 * const response = await authFetch('/api/users')
 * ```
 */
export function createAuthFetch(config: AuthFetchConfig) {
  const {
    getToken,
    getRefreshToken,
    refreshUrl,
    onTokenRefreshed,
    onAuthFailed,
    getAdditionalHeaders,
  } = config

  let isRefreshing = false
  let refreshPromise: Promise<TokenData | null> | null = null

  /**
   * Faz refresh do token
   */
  const doRefresh = async (): Promise<TokenData | null> => {
    const refreshToken = getRefreshToken()
    
    if (!refreshToken) {
      return null
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...getAdditionalHeaders?.(),
    }

    try {
      // Envia refresh_token no body conforme documentação da API
      const response = await fetch(refreshUrl, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({
          refresh_token: refreshToken,
        }),
      })

      if (!response.ok) {
        throw new Error(`Refresh failed: ${response.status}`)
      }

      const data = await response.json()
      const tokenResponse = data.data?.data || data.data || data

      const tokens: TokenData = {
        accessToken: tokenResponse.access_token || tokenResponse.accessToken,
        refreshToken: tokenResponse.refresh_token || tokenResponse.refreshToken,
        expiresAt: tokenResponse.expires_at 
          ? new Date(tokenResponse.expires_at) 
          : new Date(Date.now() + (tokenResponse.expires_in || 3600) * 1000),
        accountId: tokenResponse.account_id || tokenResponse.accountId || null,
      }

      onTokenRefreshed(tokens)
      return tokens
    } catch (error) {
      console.error('[AuthFetch] Refresh failed:', error)
      return null
    }
  }

  /**
   * Tenta refresh do token (evita múltiplas chamadas simultâneas)
   */
  const tryRefresh = async (): Promise<TokenData | null> => {
    if (isRefreshing && refreshPromise) {
      return refreshPromise
    }

    isRefreshing = true
    refreshPromise = doRefresh()

    try {
      return await refreshPromise
    } finally {
      isRefreshing = false
      refreshPromise = null
    }
  }

  /**
   * Fetch com autenticação e retry automático
   */
  const authFetch = async (
    input: RequestInfo | URL, 
    init?: RequestInit
  ): Promise<Response> => {
    // Prepara headers com token
    const prepareHeaders = (token: string | null): HeadersInit => {
      const headers = new Headers(init?.headers)
      
      if (token) {
        headers.set('Authorization', `Bearer ${token}`)
      }
      
      // Adiciona headers extras
      const additionalHeaders = getAdditionalHeaders?.()
      if (additionalHeaders) {
        Object.entries(additionalHeaders).forEach(([key, value]) => {
          headers.set(key, value)
        })
      }
      
      return headers
    }

    // Primeira tentativa
    const token = getToken()
    const response = await fetch(input, {
      ...init,
      headers: prepareHeaders(token),
      credentials: init?.credentials || 'include',
    })

    // Se não for 401, retorna diretamente
    if (response.status !== 401) {
      return response
    }

    // Tenta refresh
    console.log('[AuthFetch] Got 401, attempting token refresh...')
    const newTokens = await tryRefresh()

    if (!newTokens) {
      // Refresh falhou, chama callback de auth failed
      console.error('[AuthFetch] Token refresh failed, authentication required')
      onAuthFailed?.()
      return response
    }

    // Retry com novo token
    console.log('[AuthFetch] Token refreshed, retrying request...')
    const retryResponse = await fetch(input, {
      ...init,
      headers: prepareHeaders(newTokens.accessToken),
      credentials: init?.credentials || 'include',
    })

    // Se ainda for 401, falhou definitivamente
    if (retryResponse.status === 401) {
      console.error('[AuthFetch] Still got 401 after refresh, authentication required')
      onAuthFailed?.()
    }

    return retryResponse
  }

  return authFetch
}

/**
 * Tipo do fetch autenticado
 */
export type AuthFetch = ReturnType<typeof createAuthFetch>
